import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    MessageSquareText,
    Phone,
    MapPin,
    Wrench,
    Gauge,
    Cog,
    Disc3,
    ArrowRight,
    CheckCircle2,
    Star,
    BookOpen,
    Hammer,
    Coins,
    Users,
} from "lucide-react";
import {
    SMS_HREF,
    TEL_HREF,
    PHONE_DISPLAY,
    BRAND,
    SERVICES,
    SERVICE_AREA_LONG,
    INSTAGRAM_URL,
    PHONE_E164,
    BLOG_POSTS,
    REV_CASH_AMOUNT,
} from "@/lib/site";
import { API } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import useWorkJobs from "@/hooks/useWorkJobs";
import TestimonialsSection from "@/components/TestimonialsSection";
import { useSiteText, useServices, useBlogPosts } from "@/lib/siteConfig";
import { EditableText } from "@/lib/editMode";

const ICONS = {
    brakes: Disc3,
    suspension: Gauge,
    diagnostics: Wrench,
    installs: Cog,
};

export default function Home() {
    const { jobs } = useWorkJobs();
    const services = useServices();
    const blogPosts = useBlogPosts();
    const heroBadge = useSiteText("hero.badge", `Mobile · ${useSiteText("business.service_area_long", SERVICE_AREA_LONG)}`);
    const heroTitle = useSiteText("hero.title", BRAND);
    const heroSubtitle = useSiteText(
        "hero.subtitle",
        "Mobile mechanic & performance services in Virginia Beach. I come to you — brakes, coilovers, diagnostics, bolt-ons, done right in your driveway.",
    );
    const ctaText = useSiteText("hero.text_btn", "Text Now For A Quote");
    const phone = useSiteText("business.phone_display", PHONE_DISPLAY);
    const chips = useSiteText("hero.chips", [
        "Ford dealership tech",
        "Honest pricing",
        "Mobile — I come to you",
    ]);
    const [stats, setStats] = useState({
        jobs_done: 10,
        revcash_paid: 50,
        total_referrals: 1,
        avg_rating: 5.0,
    });

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/stats`)
            .then((r) => {
                if (mounted && r.data) setStats(r.data);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    useSEO({
        title: "Mobile Mechanic & Performance Services — Hampton Roads, VA",
        description:
            "Mobile mechanic in Virginia Beach, Norfolk, Chesapeake & Portsmouth. Brakes, coilovers, diagnostics, performance installs — done in your driveway by a Ford-trained tech.",
        image: "https://images.pexels.com/photos/13693478/pexels-photo-13693478.jpeg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            name: BRAND,
            image: "https://customer-assets.emergentagent.com/job_brake-performance-va/artifacts/kqj96fw1_image.png",
            description:
                "Mobile mechanic and performance services in Hampton Roads, VA. Brakes, suspension, coilovers, diagnostics, installs.",
            telephone: PHONE_E164,
            url: typeof window !== "undefined" ? window.location.origin : "",
            address: {
                "@type": "PostalAddress",
                addressLocality: "Virginia Beach",
                addressRegion: "VA",
                addressCountry: "US",
            },
            areaServed: [
                "Virginia Beach, VA",
                "Norfolk, VA",
                "Chesapeake, VA",
                "Portsmouth, VA",
            ],
            sameAs: [INSTAGRAM_URL],
            priceRange: "$$",
        },
    });

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0">
                    <img
                        src="https://images.pexels.com/photos/13693478/pexels-photo-13693478.jpeg"
                        alt="Ford Mustang in dark garage"
                        className="w-full h-full object-cover object-center"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/95" />
                    <div className="absolute inset-0 lbp-grid-overlay opacity-40" />
                </div>

                <div className="relative lbp-container pt-16 pb-20 sm:pt-24 sm:pb-28 lg:pt-32 lg:pb-40">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-white/15 bg-white/5 text-[11px] sm:text-xs uppercase tracking-widest text-zinc-300">
                            <MapPin className="w-3.5 h-3.5 text-red-500" />
                            <EditableText path="hero.badge" defaultValue={heroBadge} as="span" />
                        </div>

                        <EditableText
                            path="hero.title"
                            defaultValue={heroTitle}
                            as="h1"
                            className="mt-5 font-display font-black uppercase tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
                            data-testid="home-hero-title"
                        />
                        <EditableText
                            path="hero.subtitle"
                            defaultValue={heroSubtitle}
                            as="p"
                            multiline
                            className="mt-5 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed"
                            data-testid="home-hero-subtitle"
                        />

                        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <a
                                href={SMS_HREF}
                                data-testid="hero-text-now-button"
                                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 active:scale-[0.98] transition-all text-white px-7 py-4 rounded-sm font-display font-black uppercase tracking-wide text-sm sm:text-base shadow-[0_8px_30px_rgb(220,38,38,0.35)]"
                            >
                                <MessageSquareText className="w-5 h-5" />
                                <EditableText path="hero.text_btn" defaultValue={ctaText} as="span" />
                            </a>
                            <a
                                href={TEL_HREF}
                                data-testid="hero-call-now-button"
                                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-7 py-4 rounded-sm font-display font-bold uppercase tracking-wide text-sm sm:text-base"
                            >
                                <Phone className="w-5 h-5" />
                                Call {phone}
                            </a>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-zinc-300">
                            {(Array.isArray(chips) ? chips : []).map((t, i) => (
                                <span
                                    key={i}
                                    className="inline-flex items-center gap-2"
                                >
                                    <CheckCircle2 className="w-4 h-4 text-red-500" />
                                    <EditableText path={`hero.chips.${i}`} defaultValue={t} as="span" />
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* STAT STRIP — auto-updates from API */}
            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-8 sm:py-10">
                    <div
                        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
                        data-testid="home-stat-strip"
                    >
                        <Link
                            to="/gallery"
                            data-testid="home-stat-jobs"
                            className="rounded-sm border border-white/10 bg-black p-4 sm:p-5 hover:border-red-600/60 transition-colors group block"
                            aria-label="See all the jobs I've completed"
                        >
                            <div className="flex items-center gap-2 text-red-500">
                                <Hammer className="w-4 h-4" />
                                <EditableText
                                    path="home.stats.label_jobs"
                                    defaultValue="Jobs Completed"
                                    as="span"
                                    className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold"
                                />
                            </div>
                            <div className="mt-2 font-display font-black text-white text-3xl sm:text-4xl tracking-tight">
                                {stats.jobs_done}+
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-red-400 transition-colors inline-flex items-center gap-1">
                                See the work <ArrowRight className="w-3 h-3" />
                            </div>
                        </Link>
                        <Link
                            to="/feedback"
                            data-testid="home-stat-rating"
                            className="rounded-sm border border-white/10 bg-black p-4 sm:p-5 hover:border-red-600/60 transition-colors group block"
                            aria-label="Leave or read feedback"
                        >
                            <div className="flex items-center gap-2 text-red-500">
                                <Star className="w-4 h-4 fill-red-500" />
                                <EditableText
                                    path="home.stats.label_rating"
                                    defaultValue="Avg Rating"
                                    as="span"
                                    className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold"
                                />
                            </div>
                            <div className="mt-2 font-display font-black text-white text-3xl sm:text-4xl tracking-tight inline-flex items-baseline gap-1">
                                {stats.avg_rating.toFixed(1)}
                                <span className="text-zinc-500 text-base font-bold">
                                    / 5
                                </span>
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-red-400 transition-colors inline-flex items-center gap-1">
                                Leave feedback <ArrowRight className="w-3 h-3" />
                            </div>
                        </Link>
                        <Link
                            to="/refer"
                            data-testid="home-stat-revcash"
                            className="rounded-sm border border-white/10 bg-black p-4 sm:p-5 hover:border-red-600/60 transition-colors group block"
                            aria-label="Earn Rev Cash by referring friends"
                        >
                            <div className="flex items-center gap-2 text-red-500">
                                <Coins className="w-4 h-4" />
                                <EditableText
                                    path="home.stats.label_revcash"
                                    defaultValue="Rev Cash Paid"
                                    as="span"
                                    className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold"
                                />
                            </div>
                            <div className="mt-2 font-display font-black text-white text-3xl sm:text-4xl tracking-tight">
                                ${stats.revcash_paid}
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-red-400 transition-colors inline-flex items-center gap-1">
                                Refer & earn <ArrowRight className="w-3 h-3" />
                            </div>
                        </Link>
                        <Link
                            to="/refer"
                            data-testid="home-stat-referrals"
                            className="rounded-sm border border-white/10 bg-black p-4 sm:p-5 hover:border-red-600/60 transition-colors group block"
                            aria-label="Refer a friend"
                        >
                            <div className="flex items-center gap-2 text-red-500">
                                <Users className="w-4 h-4" />
                                <EditableText
                                    path="home.stats.label_referrals"
                                    defaultValue="Friends Referred"
                                    as="span"
                                    className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold"
                                />
                            </div>
                            <div className="mt-2 font-display font-black text-white text-3xl sm:text-4xl tracking-tight">
                                {stats.total_referrals}
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-red-400 transition-colors inline-flex items-center gap-1">
                                Send a friend <ArrowRight className="w-3 h-3" />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            {/* SERVICES QUICK LIST */}
            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-16 sm:py-20">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <EditableText
                                path="home.services_section.eyebrow"
                                defaultValue="What I do"
                                as="p"
                                className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                            />
                            <EditableText
                                path="home.services_section.title"
                                defaultValue="Services"
                                as="h2"
                                className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl"
                            />
                        </div>
                        <Link
                            to="/services"
                            data-testid="home-services-view-all"
                            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-zinc-300 hover:text-red-500"
                        >
                            <EditableText path="home.services_section.view_all" defaultValue="View All" as="span" /> <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="mt-10 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                        {services.map((svc) => {
                            const Icon = ICONS[svc.slug] || Wrench;
                            return (
                                <Link
                                    key={svc.slug}
                                    to="/services"
                                    data-testid={`home-service-card-${svc.slug}`}
                                    className="group relative overflow-hidden rounded-sm border border-white/10 bg-zinc-900 p-6 hover:border-red-600 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <Icon className="w-7 h-7 text-red-500" />
                                        <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-red-500 group-hover:translate-x-1 transition-all" />
                                    </div>
                                    <EditableText
                                        path={`services.${svc.slug}.title`}
                                        defaultValue={svc.title}
                                        as="h3"
                                        className="mt-6 font-display font-bold uppercase tracking-tight text-white text-xl"
                                    />
                                    <EditableText
                                        path={`services.${svc.slug}.short`}
                                        defaultValue={svc.short}
                                        multiline
                                        as="p"
                                        className="mt-2 text-sm text-zinc-400 leading-relaxed"
                                    />
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* JOB PHOTOS STRIP */}
            <section className="bg-black border-b border-white/10">
                <div className="lbp-container py-16 sm:py-20">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <EditableText
                                path="home.gallery_section.eyebrow"
                                defaultValue="Recent Work"
                                as="p"
                                className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                            />
                            <EditableText
                                path="home.gallery_section.title"
                                defaultValue="From The Driveway"
                                as="h2"
                                className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl"
                            />
                        </div>
                        <Link
                            to="/gallery"
                            data-testid="home-gallery-view-all"
                            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-zinc-300 hover:text-red-500"
                        >
                            <EditableText path="home.gallery_section.view_all" defaultValue="See All Work" as="span" /> <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="mt-10 grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-3">
                        {jobs.slice(0, 3).map((g) => (
                            <Link
                                to={`/work/${g.slug}`}
                                key={g.slug}
                                className="group relative overflow-hidden rounded-sm aspect-[4/5] md:aspect-[4/3] block"
                                data-testid={`home-gallery-item-${g.slug}`}
                            >
                                <img
                                    src={g.image}
                                    alt={g.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                                <figcaption className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                                    <div className="text-[11px] uppercase tracking-widest text-red-400 font-semibold">
                                        Job · Tap to see breakdown
                                    </div>
                                    <div className="mt-1 font-display font-bold text-white text-base sm:text-lg leading-snug">
                                        {g.title}
                                    </div>
                                </figcaption>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRUST SECTION */}
            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <EditableText
                            path="home.trust.eyebrow"
                            defaultValue="Why Me"
                            as="p"
                            className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                        />
                        <EditableText
                            path="home.trust.title"
                            defaultValue="Real Work. Fair Price. No Bullshit."
                            as="h2"
                            className="mt-2 font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl"
                        />
                        <EditableText
                            path="home.trust.body"
                            defaultValue="I work professionally on cars every day at a Ford dealership and I'm building this brand on my own time — which means you get dealership-grade diagnostics without dealership-grade labor rates. I'll tell you straight what your car needs and what it doesn't."
                            multiline
                            as="p"
                            className="mt-5 text-zinc-400 leading-relaxed max-w-xl"
                        />

                        <div className="mt-8 grid sm:grid-cols-2 gap-4">
                            {[
                                { t: "Dealer-trained", d: "Ford dealership technician with real diagnostic experience." },
                                { t: "Mobile service", d: "I bring the tools to your driveway across Hampton Roads." },
                                { t: "Straight talk", d: "No upsells you don't need. Ever." },
                                { t: "Performance-minded", d: "Coilovers, bolt-ons, tuning — done properly." },
                            ].map((item, i) => (
                                <div
                                    key={i}
                                    className="rounded-sm border border-white/10 bg-zinc-900 p-5"
                                >
                                    <div className="flex items-center gap-2">
                                        <Star className="w-4 h-4 text-red-500" />
                                        <EditableText
                                            path={`home.trust.items.${i}.t`}
                                            defaultValue={item.t}
                                            as="h3"
                                            className="font-display font-bold uppercase tracking-wide text-white text-sm"
                                        />
                                    </div>
                                    <EditableText
                                        path={`home.trust.items.${i}.d`}
                                        defaultValue={item.d}
                                        multiline
                                        as="p"
                                        className="mt-2 text-sm text-zinc-400 leading-relaxed"
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mt-8">
                            <Link
                                to="/about"
                                data-testid="home-learn-more-about"
                                className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white hover:text-red-500"
                            >
                                <EditableText path="home.trust.more_link" defaultValue="More about me" as="span" />{" "}
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden rounded-sm border border-white/10">
                        <img
                            src="https://customer-assets.emergentagent.com/job_brake-performance-va/artifacts/erg2gqdo_IMG_7211.jpeg"
                            alt="Engine pulled in customer's driveway"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <TestimonialsSection />

            {/* REFER + BLOG TEASERS */}
            <section className="bg-black border-b border-white/10">
                <div className="lbp-container py-14 sm:py-20 grid md:grid-cols-2 gap-4 sm:gap-6">
                    {/* REFER TEASER */}
                    <Link
                        to="/refer"
                        data-testid="home-refer-teaser"
                        className="group relative overflow-hidden rounded-sm border border-red-600/40 bg-gradient-to-br from-red-600/15 via-zinc-950 to-zinc-950 p-6 sm:p-8 hover:border-red-600 transition-colors block"
                    >
                        <div className="text-[11px] uppercase tracking-widest text-red-500 font-bold inline-flex items-center gap-2">
                            <Star className="w-3.5 h-3.5" />
                            <EditableText path="home.refer_teaser.badge" defaultValue="Rev Cash Program" as="span" />
                        </div>
                        <h3 className="mt-3 font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl leading-tight">
                            <EditableText path="home.refer_teaser.title_line1" defaultValue="Refer A Friend." as="span" /><br />
                            <span className="text-red-500">
                                <EditableText path="home.refer_teaser.title_line2" defaultValue={`Get $${REV_CASH_AMOUNT} Rev Cash.`} as="span" />
                            </span>
                        </h3>
                        <EditableText
                            path="home.refer_teaser.body"
                            defaultValue={`Send your buddy our way. When they get a job done, you bank $${REV_CASH_AMOUNT} in credit toward your next service. No cap on how many friends you can refer.`}
                            multiline
                            as="p"
                            className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base"
                        />
                        <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-white">
                            <EditableText path="home.refer_teaser.cta" defaultValue="How it works" as="span" /> <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </span>
                    </Link>

                    {/* BLOG TEASER */}
                    <Link
                        to="/articles"
                        data-testid="home-blog-teaser"
                        className="group relative overflow-hidden rounded-sm border border-white/10 bg-zinc-900 hover:border-white/30 transition-colors block"
                    >
                        <div className="absolute inset-0 opacity-30">
                            <img
                                src={blogPosts[0]?.cover}
                                alt=""
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 via-zinc-900/70 to-transparent" />
                        </div>
                        <div className="relative p-6 sm:p-8">
                            <div className="text-[11px] uppercase tracking-widest text-red-500 font-bold inline-flex items-center gap-2">
                                <BookOpen className="w-3.5 h-3.5" />
                                <EditableText path="home.blog_teaser.badge" defaultValue="Latest Article" as="span" />
                            </div>
                            {blogPosts[0] && (
                                <EditableText
                                    path={`blog.${blogPosts[0].slug}.title`}
                                    defaultValue={blogPosts[0].title}
                                    as="h3"
                                    className="mt-3 font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl leading-tight"
                                />
                            )}
                            {blogPosts[0] && (
                                <EditableText
                                    path={`blog.${blogPosts[0].slug}.excerpt`}
                                    defaultValue={blogPosts[0].excerpt}
                                    multiline
                                    as="p"
                                    className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base line-clamp-3"
                                />
                            )}
                            <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wider text-white">
                                <EditableText path="home.blog_teaser.cta" defaultValue="Read article" as="span" /> <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </span>
                        </div>
                    </Link>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-black">
                <div className="lbp-container py-16 sm:py-24 text-center">
                    <EditableText
                        path="home.final_cta.title"
                        defaultValue="Got A Job In Mind? Shoot Me A Text."
                        as="h2"
                        className="font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl max-w-3xl mx-auto"
                    />
                    <EditableText
                        path="home.final_cta.body"
                        defaultValue="Quick quotes, honest answers. Text is always fastest — I'll get back to you same day."
                        multiline
                        as="p"
                        className="mt-4 text-zinc-400 max-w-xl mx-auto"
                    />
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <a
                            href={SMS_HREF}
                            data-testid="home-final-text-button"
                            className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 active:scale-[0.98] transition-all text-white px-7 py-4 rounded-sm font-display font-black uppercase tracking-wide text-sm sm:text-base"
                        >
                            <MessageSquareText className="w-5 h-5" />
                            Text {phone}
                        </a>
                        <a
                            href={TEL_HREF}
                            data-testid="home-final-call-button"
                            className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-7 py-4 rounded-sm font-display font-bold uppercase tracking-wide text-sm sm:text-base"
                        >
                            <Phone className="w-5 h-5" />
                            <EditableText path="home.final_cta.call_btn" defaultValue="Call Instead" as="span" />
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
